import { FC } from "react";
import { Button, ButtonProps } from "./index";

/**
 * Thin wrappers for common button use cases
 * These provide convenient presets while maintaining full customizability
 */

export type SubmitButtonProps = Omit<ButtonProps, "variant" | "variantColor"> & {
  variant?: ButtonProps["variant"];
  variantColor?: ButtonProps["variantColor"];
};

/**
 * SubmitButton - Optimized for form submissions
 * Default: Primary blue button, commonly used for submitting forms
 */
export const SubmitButton: FC<SubmitButtonProps> = ({
  variant = "primary",
  variantColor = "blue",
  label = "Submit",
  ...props
}) => <Button variant={variant} variantColor={variantColor} label={label} {...props} />;

export type DeleteButtonProps = Omit<ButtonProps, "variant" | "variantColor"> & {
  variant?: ButtonProps["variant"];
  variantColor?: ButtonProps["variantColor"];
};

/**
 * DangerButton - For destructive actions
 * Default: Primary danger button, used for delete/destructive operations
 */
export const DeleteButton: FC<DeleteButtonProps> = ({
  variant = "primary",
  variantColor = "danger",
  label = "Delete",
  ...props
}) => <Button variant={variant} variantColor={variantColor} label={label} {...props} />;

export type CancelButtonProps = Omit<ButtonProps, "variant" | "variantColor"> & {
  variant?: ButtonProps["variant"];
  variantColor?: ButtonProps["variantColor"];
};

/**
 * CancelButton - For cancel/dismiss actions
 * Default: Secondary grey button, commonly used for cancelling operations
 */
export const CancelButton: FC<CancelButtonProps> = ({
  variant = "primary",
  variantColor = "grey",
  label = "Cancel",
  ...props
}) => <Button variant={variant} variantColor={variantColor} label={label} {...props} />;

export type LinkButtonProps = Omit<ButtonProps, "variant"> & {
  variant?: ButtonProps["variant"];
};

/**
 * LinkButton - For link-style actions
 * Default: Link variant with no background, used for secondary actions
 */
export const LinkButton: FC<LinkButtonProps> = ({
  label = "Link",
  variant = "link",
  variantColor = "blue",
  ...props
}) => <Button variant={variant} variantColor={variantColor} label={label} {...props} />;

export type SaveButtonProps = Omit<ButtonProps, "variant" | "variantColor"> & {
  variant?: ButtonProps["variant"];
  variantColor?: ButtonProps["variantColor"];
};

/**
 * SaveButton - For save operations
 * Default: Primary success button, commonly used for saving changes
 */
export const SaveButton: FC<SaveButtonProps> = ({
  variant = "primary",
  variantColor = "blue",
  label = "Save",
  ...props
}) => <Button variant={variant} variantColor={variantColor} label={label} {...props} />;

// Export all wrappers for convenience
export const ButtonWrappers = {
  Submit: SubmitButton,
  Delete: DeleteButton,
  Cancel: CancelButton,
  Link: LinkButton,
  Save: SaveButton,
};
